import { type ReactNode, useMemo } from 'react';
import { FaChevronRight } from 'react-icons/fa';
import StatusBadge from '@/components/UI/StatusBadge';
import Tooltip from '@/components/UI/Tooltip';
import {
    type AlphaAtlasCombinationDetail,
    type AlphaAtlasConditionKind,
    type AlphaAtlasReportRow,
    type AlphaAtlasWindowRole,
    alphaAtlasDirectionLabel,
    CONDITION_KIND_META,
    describeWindowConsistency,
    EVIDENCE_STATE_META,
    WINDOW_CONSISTENCY_META,
    WINDOW_ROLE_META
} from './AlphaAtlasReportState';
import {
    formatCount,
    formatHorizon,
    formatMarketDate,
    formatPercentMicros,
    humanizeCatalogLabel,
    TONE_TEXT_CLASS,
    toneForMicros
} from './alphaAtlasFormat';

interface AlphaAtlasCombinationResultsProps {
    detail: AlphaAtlasCombinationDetail;
    onSelectRow: (row: AlphaAtlasReportRow) => void;
}

const CONTROLLED_WINDOWS: AlphaAtlasWindowRole[] = ['training', 'selectionValidation'];
const TH = 'px-3 py-2 text-xs font-medium uppercase tracking-wider text-zinc-400 whitespace-nowrap';
const TD = 'px-3 py-2 align-top text-sm tabular-nums whitespace-nowrap';

const rowKey = (windowRole: AlphaAtlasWindowRole, horizonSeconds: number, conditionKind: AlphaAtlasConditionKind): string =>
    `${windowRole}|${horizonSeconds}|${conditionKind}`;

const Signed = ({ micros, className = '' }: { micros: number | null; className?: string }) => (
    <span className={`${TONE_TEXT_CLASS[toneForMicros(micros)]} ${className}`}>{formatPercentMicros(micros, { signed: true })}</span>
);

const WindowCells = ({
    row,
    showLift,
    onSelectRow
}: {
    row: AlphaAtlasReportRow | undefined;
    showLift: boolean;
    onSelectRow: (row: AlphaAtlasReportRow) => void;
}) => {
    if (!row) {
        const blanks = showLift ? 7 : 5;
        return (
            <>
                {Array.from({ length: blanks }, (_, index) => (
                    <td className={`${TD} text-right text-zinc-600`} key={index}>
                        {index === 0 ? 'No result' : ''}
                    </td>
                ))}
            </>
        );
    }
    return (
        <>
            <td className={`${TD} text-right`}>
                <Signed micros={row.netMeanMicros} />
            </td>
            <td className={`${TD} text-right`}>
                <Signed className="font-semibold" micros={row.randomEdgeMeanMicros} />
            </td>
            {showLift && (
                <td className={`${TD} text-right`}>
                    <Signed micros={row.liftVsTriggerMicros} />
                </td>
            )}
            {showLift && (
                <td className={`${TD} text-right`}>
                    <Signed micros={row.liftVsContextMicros} />
                </td>
            )}
            <td className={`${TD} text-right text-zinc-200`}>{formatCount(row.supportCount)}</td>
            <td className={TD}>
                <Tooltip content={EVIDENCE_STATE_META[row.evidenceState].description}>
                    <StatusBadge status={row.evidenceState} tone={EVIDENCE_STATE_META[row.evidenceState].tone}>
                        {EVIDENCE_STATE_META[row.evidenceState].label}
                    </StatusBadge>
                </Tooltip>
            </td>
            <td className={`${TD} text-right`}>
                <button
                    aria-label={`Open ${WINDOW_ROLE_META[row.windowRole].shortLabel} details for the ${formatHorizon(row.horizonSeconds)} horizon`}
                    className="rounded p-1 text-zinc-500 hover:bg-zinc-800 hover:text-white"
                    type="button"
                    onClick={() => onSelectRow(row)}
                >
                    <FaChevronRight className="h-3 w-3" />
                </button>
            </td>
        </>
    );
};

const WindowHeaderCells = ({ showLift }: { showLift: boolean }) => (
    <>
        <th className={`${TH} text-right`} scope="col">
            Net
        </th>
        <th className={`${TH} text-right`} scope="col">
            Edge vs random
        </th>
        {showLift && (
            <th className={`${TH} text-right`} scope="col">
                Lift vs trigger
            </th>
        )}
        {showLift && (
            <th className={`${TH} text-right`} scope="col">
                Lift vs context
            </th>
        )}
        <th className={`${TH} text-right`} scope="col">
            Support
        </th>
        <th className={TH} scope="col">
            Evidence
        </th>
        <th className={TH} scope="col">
            <span className="sr-only">Details</span>
        </th>
    </>
);

const ResultsTable = ({
    title,
    description,
    conditionKind,
    detail,
    rowsByKey,
    onSelectRow
}: {
    title: ReactNode;
    description?: string;
    conditionKind: AlphaAtlasConditionKind;
    detail: AlphaAtlasCombinationDetail;
    rowsByKey: Map<string, AlphaAtlasReportRow>;
    onSelectRow: (row: AlphaAtlasReportRow) => void;
}) => {
    const showLift = conditionKind === 'parentAndChild';
    const windowSpan = showLift ? 7 : 5;
    return (
        <section className="space-y-2">
            <div>
                <h4 className="text-sm font-semibold text-zinc-100">{title}</h4>
                {description && <p className="text-xs text-zinc-500">{description}</p>}
            </div>
            <div className="overflow-x-auto rounded-lg border border-zinc-800">
                <table className="w-full min-w-[980px] text-left">
                    <thead className="bg-[#131416]">
                        <tr className="border-b border-zinc-800">
                            <th className={TH} rowSpan={2} scope="col">
                                Horizon
                            </th>
                            {CONTROLLED_WINDOWS.map((windowRole) => (
                                <th
                                    className={`${TH} border-l border-zinc-800 text-center`}
                                    colSpan={windowSpan}
                                    key={windowRole}
                                    scope="colgroup"
                                >
                                    <Tooltip content={WINDOW_ROLE_META[windowRole].description}>
                                        <span className="cursor-help underline decoration-dotted decoration-zinc-600 underline-offset-4">
                                            {WINDOW_ROLE_META[windowRole].label}
                                        </span>
                                    </Tooltip>
                                </th>
                            ))}
                            <th className={`${TH} border-l border-zinc-800`} rowSpan={2} scope="col">
                                <Tooltip content="Whether the edge vs random entry is positive in both controlled windows. The strongest robustness signal available without revealing verification evidence.">
                                    <span className="cursor-help underline decoration-dotted decoration-zinc-600 underline-offset-4">
                                        Across windows
                                    </span>
                                </Tooltip>
                            </th>
                        </tr>
                        <tr>
                            {CONTROLLED_WINDOWS.map((windowRole) => (
                                <WindowHeaderCells key={windowRole} showLift={showLift} />
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                        {detail.horizons.map((horizon) => {
                            const historical = rowsByKey.get(rowKey('training', horizon.horizonSeconds, conditionKind));
                            const validation = rowsByKey.get(rowKey('selectionValidation', horizon.horizonSeconds, conditionKind));
                            const consistency = describeWindowConsistency(historical, validation);
                            return (
                                <tr className="hover:bg-zinc-800/30" key={horizon.horizonSeconds}>
                                    <td className={`${TD} font-medium text-zinc-100`}>
                                        {formatHorizon(horizon.horizonSeconds)}
                                        {horizon.matureDecisionEndAtUnix !== null && (
                                            <span className="block text-xs font-normal text-zinc-500">
                                                mature through {formatMarketDate(horizon.matureDecisionEndAtUnix)}
                                            </span>
                                        )}
                                    </td>
                                    <WindowCells row={historical} showLift={showLift} onSelectRow={onSelectRow} />
                                    <WindowCells row={validation} showLift={showLift} onSelectRow={onSelectRow} />
                                    <td className={`${TD} border-l border-zinc-800`}>
                                        <Tooltip content={WINDOW_CONSISTENCY_META[consistency].description}>
                                            <StatusBadge status={consistency} tone={WINDOW_CONSISTENCY_META[consistency].tone}>
                                                {WINDOW_CONSISTENCY_META[consistency].label}
                                            </StatusBadge>
                                        </Tooltip>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
};

/**
 * One combination across every horizon, with the two controlled windows side by side and the
 * trigger-only / context-only comparators underneath.
 */
const AlphaAtlasCombinationResults = ({ detail, onSelectRow }: AlphaAtlasCombinationResultsProps) => {
    const rowsByKey = useMemo(
        () => new Map(detail.results.map((row) => [rowKey(row.windowRole, row.horizonSeconds, row.conditionKind), row])),
        [detail.results]
    );
    const primaryKind: AlphaAtlasConditionKind =
        detail.parent && detail.child ? 'parentAndChild' : detail.parent ? 'parentOnly' : 'childOnly';
    const parentName = detail.parent ? humanizeCatalogLabel(detail.parent.label) : null;
    const childName = detail.child ? humanizeCatalogLabel(detail.child.label) : null;
    const title = parentName && childName ? `${parentName} + ${childName}` : (parentName ?? childName ?? 'Combination');
    const historicalWindow = detail.windows.find((window) => window.windowRole === 'training');
    const validationWindow = detail.windows.find((window) => window.windowRole === 'selectionValidation');

    return (
        <div className="space-y-6">
            <div className="space-y-1">
                <h3 className="text-lg font-semibold text-white">{title}</h3>
                <p className="text-sm text-zinc-400">
                    {CONDITION_KIND_META[primaryKind].label} · {detail.entityLabel} ·{' '}
                    {alphaAtlasDirectionLabel(detail.source.outcomeKind, detail.direction)}
                </p>
                {historicalWindow && validationWindow && (
                    <p className="text-xs text-zinc-500">
                        Historical {formatMarketDate(historicalWindow.startAtUnix)} – {formatMarketDate(historicalWindow.endAtUnix)} ·
                        Selection validation {formatMarketDate(validationWindow.startAtUnix)} –{' '}
                        {formatMarketDate(validationWindow.endAtUnix)} · verification stays sealed here
                    </p>
                )}
            </div>

            <ResultsTable
                conditionKind={primaryKind}
                description={
                    primaryKind === 'parentAndChild'
                        ? 'Lift vs trigger shows what the context adds. Lift vs context shows what the trigger adds.'
                        : undefined
                }
                detail={detail}
                rowsByKey={rowsByKey}
                title="By horizon"
                onSelectRow={onSelectRow}
            />

            {primaryKind === 'parentAndChild' && (
                <div className="grid gap-6 xl:grid-cols-1">
                    <ResultsTable
                        conditionKind="parentOnly"
                        description={`${parentName} on its own, regardless of context. The baseline the context should improve on.`}
                        detail={detail}
                        rowsByKey={rowsByKey}
                        title="Trigger alone"
                        onSelectRow={onSelectRow}
                    />
                    <ResultsTable
                        conditionKind="childOnly"
                        description={`${childName} on its own, regardless of trigger.`}
                        detail={detail}
                        rowsByKey={rowsByKey}
                        title="Context alone"
                        onSelectRow={onSelectRow}
                    />
                </div>
            )}
        </div>
    );
};

export default AlphaAtlasCombinationResults;
